import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, Search, Printer, FileText, ShieldCheck, Ban, Fuel, Ship } from 'lucide-react';
import { PageShell, primaryButton, ghostButton, inputStyle, glassLight, T, Badge, StatCard, EmptyState, Field, SectionTitle, usePagination, Pagination, useIsMobile, CampoValidado, useConfirmacion } from '../ui';
import { COMPARTIMIENTOS, PRODUCTOS_WOP, SUPLIDORES, TIPOS_DISPONIBLES, esWOP, etiquetaTipo, fmtFecha, fmtGal, imprimirPedido, normalizarTipo, nuevoToken } from './pedidoDocumento';
import type { Camion, Cola, Conductor, Envasadora, Pedido, PedidoLinea, PedidoTipo, ProductoWOP } from '../types';

// Ícono con el que se reconoce cada suplidor de un vistazo. Coastal despacha
// por terminal marítima, de ahí el barco.
const ICONO_TIPO: Record<PedidoTipo, React.ElementType> = {
  GLP_REFINERIA: Fuel,
  GLP_COASTAL: Ship,
  WOP: Fuel
};
// Color del distintivo en el listado, para distinguir a qué suplidor fue cada
// orden sin tener que leer la etiqueta.
const COLOR_TIPO: Record<PedidoTipo, {
  color: string;
  soft: string;
}> = {
  GLP_REFINERIA: {
    color: T.brand,
    soft: T.accentSoft
  },
  GLP_COASTAL: {
    color: T.info,
    soft: T.infoSoft
  },
  WOP: {
    color: T.textMuted,
    soft: T.surfaceSubtle
  }
};

// Fecha en yyyy-mm-dd tomada del calendario LOCAL. `toISOString()` convierte a
// UTC, y como República Dominicana va cuatro horas atrás, después de las 8 de
// la noche devolvía el día siguiente: las órdenes de la tarde quedaban
// fechadas mañana.
const isoLocal = (d: Date) => {
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
};
const hoyISO = () => isoLocal(new Date());

// Atajos del filtro de fecha. La semana arranca el lunes.
type RangoFecha = 'todas' | 'hoy' | 'semana' | 'mes';
const RANGOS: {
  id: RangoFecha;
  label: string;
}[] = [{
  id: 'todas',
  label: 'Todas'
}, {
  id: 'hoy',
  label: 'Hoy'
}, {
  id: 'semana',
  label: 'Esta semana'
}, {
  id: 'mes',
  label: 'Este mes'
}];
const limitesRango = (modo: RangoFecha): {
  desde: string;
  hasta: string;
} | null => {
  if (modo === 'todas') return null;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  if (modo === 'hoy') {
    return {
      desde: isoLocal(hoy),
      hasta: isoLocal(hoy)
    };
  }
  if (modo === 'semana') {
    const desdeLunes = (hoy.getDay() + 6) % 7; // domingo (0) cuenta como 6
    const lunes = new Date(hoy);
    lunes.setDate(hoy.getDate() - desdeLunes);
    const domingo = new Date(lunes);
    domingo.setDate(lunes.getDate() + 6);
    return {
      desde: isoLocal(lunes),
      hasta: isoLocal(domingo)
    };
  }
  const primero = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const ultimo = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
  return {
    desde: isoLocal(primero),
    hasta: isoLocal(ultimo)
  };
};
const totalGalones = (p: Pedido) => p.lineas.reduce((sum, l) => sum + (l.galones || 0), 0);
const estadoStyle = (estado: Pedido['estado']) => {
  if (estado === 'autorizado') return {
    color: T.ok,
    soft: T.okSoft,
    label: 'Autorizado'
  };
  if (estado === 'anulado') return {
    color: T.danger,
    soft: T.dangerSoft,
    label: 'Anulado'
  };
  return {
    color: T.warn,
    soft: T.warnSoft,
    label: 'Borrador'
  };
};
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};

// Línea vacía para un compartimiento que todavía no se llenó.
const lineaVacia = (compartimiento: number): PedidoLinea => ({
  compartimiento,
  producto: 'gasoilRegular',
  galones: 0,
  destino: '',
  rncDestino: ''
});
const lineaGLPVacia = (): PedidoLinea => ({
  compartimiento: 1,
  producto: 'glp',
  galones: 0,
  destino: '',
  rncDestino: ''
});
const pedidoVacio = (): Partial<Pedido> => ({
  tipo: 'GLP_REFINERIA',
  numeroOrden: '',
  fecha: hoyISO(),
  conductor: '',
  paseChofer: '',
  fichaCola: '',
  placaCamion: '',
  lineas: [lineaGLPVacia()],
  estado: 'borrador',
  observaciones: ''
});
interface PedidosProps {
  pedidos: Pedido[];
  onAdd: (p: Pedido) => void;
  onUpdate: (p: Pedido) => void;
  onDelete: (id: string) => void;
  // Catálogos de "Flota y Destinos". Alimentan los desplegables del
  // formulario: elegir un conductor trae su pase, su placa, su cola y sus
  // galones; elegir una envasadora trae su RNC. Se mantienen en su propio
  // módulo, aquí solo se consultan.
  conductores: Conductor[];
  envasadoras: Envasadora[];
  camiones: Camion[];
  colas: Cola[];
  // Si el usuario no tiene permiso de ver Flota, no tiene sentido mandarlo
  // allá cuando las listas están vacías.
  puedeVerFlota: boolean;
  // Permisos resueltos por el rol del usuario (ver App.tsx).
  canEdit: boolean;
  canArchive: boolean;
  canAutorizar: boolean;
  // Quién firma cuando se autoriza una orden.
  usuarioNombre: string;
  usuarioCargo: string;
  onLog?: (mensaje: string) => void;
}
export function Pedidos({
  pedidos,
  onAdd,
  onUpdate,
  onDelete,
  // Los catálogos llevan valor por defecto a propósito: si por lo que sea
  // llegan sin definir (App.tsx sin actualizar, caché a medias), el módulo
  // sigue en pie y se comporta como si no hubiera nada registrado, en vez de
  // dejar la pantalla en blanco.
  conductores = [],
  envasadoras = [],
  camiones = [],
  colas = [],
  puedeVerFlota = false,
  canEdit,
  canArchive,
  canAutorizar,
  usuarioNombre,
  usuarioCargo,
  onLog
}: PedidosProps) {
  const isMobile = useIsMobile();
  const [dialogo, confirmar] = useConfirmacion();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState<'todos' | PedidoTipo>('todos');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'borrador' | 'autorizado' | 'anulado'>('todos');
  const [filterFecha, setFilterFecha] = useState<RangoFecha>('todas');
  const [form, setForm] = useState<Partial<Pedido>>(pedidoVacio());
  const lineas = form.lineas ?? [];

  // ── FILTRADO ──────────────────────────────────────────────────────────
  // Las fechas son yyyy-mm-dd, así que comparar con < y > alfabéticamente da
  // el mismo resultado que comparar como fechas y evita construir Dates.
  const filtrados = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const rango = limitesRango(filterFecha);
    return pedidos.filter(p => filterTipo === 'todos' || normalizarTipo(p.tipo) === filterTipo).filter(p => filterEstado === 'todos' || p.estado === filterEstado).filter(p => !rango || !!p.fecha && p.fecha >= rango.desde && p.fecha <= rango.hasta).filter(p => {
      if (!q) return true;
      const enDestinos = p.lineas.some(l => l.destino.toLowerCase().includes(q) || l.rncDestino.toLowerCase().includes(q));
      return p.numeroOrden.toLowerCase().includes(q) || p.conductor.toLowerCase().includes(q) || p.placaCamion.toLowerCase().includes(q) || enDestinos;
    }).sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
  }, [pedidos, searchTerm, filterTipo, filterEstado, filterFecha]);

  // Cuántas órdenes caen en cada atajo de fecha: saberlo antes de hacer clic
  // ahorra el viaje de ir y volver cuando el rango está vacío.
  const conteoRango = useMemo(() => {
    const cuenta = {} as Record<RangoFecha, number>;
    for (const r of RANGOS) {
      const lim = limitesRango(r.id);
      cuenta[r.id] = lim ? pedidos.filter(p => !!p.fecha && p.fecha >= lim.desde && p.fecha <= lim.hasta).length : pedidos.length;
    }
    return cuenta;
  }, [pedidos]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filtrados, 6);

  // ── ESTADÍSTICAS ──────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const vivos = pedidos.filter(p => p.estado !== 'anulado');
    const mes = hoyISO().slice(0, 7);
    return {
      total: pedidos.length,
      borradores: pedidos.filter(p => p.estado === 'borrador').length,
      autorizados: pedidos.filter(p => p.estado === 'autorizado').length,
      galonesMes: vivos.filter(p => (p.fecha || '').startsWith(mes)).reduce((s, p) => s + totalGalones(p), 0)
    };
  }, [pedidos]);

  // WOP ya no se ofrece al crear, pero si quedan órdenes viejas del formato
  // hace falta poder filtrarlas. El filtro aparece solo si existen.
  const hayWOP = useMemo(() => pedidos.some(p => esWOP(p.tipo)), [pedidos]);

  // ── FORMULARIO ────────────────────────────────────────────────────────
  const abrirNuevo = () => {
    setForm(pedidoVacio());
    setEditingId(null);
    setShowForm(true);
  };
  const abrirEdicion = (p: Pedido) => {
    setForm({
      ...p,
      lineas: p.lineas.map(l => ({
        ...l
      }))
    });
    setEditingId(p.id);
    setShowForm(true);
  };
  const cerrarForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(pedidoVacio());
  };

  // Cambiar de suplidor no toca el detalle: los dos formatos de GLP son el
  // mismo papel. Solo hay que rehacer las líneas si se viene de una orden
  // vieja de WOP, porque sus 6 compartimientos con producto no son
  // intercambiables con la línea única de GLP.
  const cambiarTipo = (tipo: PedidoTipo) => {
    setForm(prev => {
      const veniaDeWOP = esWOP(prev.tipo ?? '');
      const vaAWOP = esWOP(tipo);
      if (veniaDeWOP === vaAWOP) return {
        ...prev,
        tipo
      };
      return {
        ...prev,
        tipo,
        lineas: vaAWOP ? COMPARTIMIENTOS.map(lineaVacia) : [lineaGLPVacia()]
      };
    });
  };
  // ── CATÁLOGOS Y REGLAS ────────────────────────────────────────────────
  const conductoresActivos = useMemo(() => (conductores ?? []).filter(c => c.activo !== false).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')), [conductores]);
  const envasadorasActivas = useMemo(() => (envasadoras ?? []).filter(e => e.activo !== false).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')), [envasadoras]);
  const camionesActivos = useMemo(() => (camiones ?? []).filter(c => c.activo !== false), [camiones]);
  const colasActivas = useMemo(() => (colas ?? []).filter(c => c.activo !== false), [colas]);

  // El chofer de una orden vieja puede no estar registrado (o haberse dado de
  // baja). Se le agrega su propia opción para que el desplegable no lo pierda
  // al editar.
  const conductorFuera = !!form.conductor?.trim() && !conductoresActivos.some(c => c.nombre === form.conductor);
  const conductorActual = useMemo(() => conductoresActivos.find(c => c.nombre === form.conductor), [conductoresActivos, form.conductor]);
  // El destino manda: de él dependen qué colas y qué camiones se pueden usar.
  const destinoActual = useMemo(() => envasadorasActivas.find(e => e.nombre === (form.lineas?.[0]?.destino ?? '')), [envasadorasActivas, form.lineas]);

  // Un conductor sin unidades asignadas puede llevar cualquiera de las que
  // estén en servicio; con unidades asignadas, solo las suyas.
  const camionesDelConductor = useMemo(() => {
    const suyosLista = conductorActual?.camiones ?? [];
    if (!suyosLista.length) return camionesActivos;
    const suyos = new Set(suyosLista.map(k => k.id));
    return camionesActivos.filter(c => suyos.has(c.id));
  }, [conductorActual, camionesActivos]);
  const colasDelConductor = useMemo(() => {
    const suyasLista = conductorActual?.colas ?? [];
    if (!suyasLista.length) return colasActivas;
    const suyas = new Set(suyasLista.map(k => k.id));
    return colasActivas.filter(c => suyas.has(c.id));
  }, [conductorActual, colasActivas]);

  // Camiones que pueden hacer ESTE viaje: ni vetados en la planta de destino,
  // ni reservados a otro chofer.
  const camionesValidos = useMemo(() => camionesDelConductor.filter(c => (!destinoActual || !(c.plantasVetadas ?? []).includes(destinoActual.id)) && (!c.soloConductorId || c.soloConductorId === conductorActual?.id)), [camionesDelConductor, destinoActual, conductorActual]);

  // Colas admitidas por la planta, separadas en primera y segunda opción.
  // Sin destino elegido todavía, se ofrecen todas las del conductor.
  const {
    colasPrimera,
    colasSegunda
  } = useMemo(() => {
    if (!destinoActual) return {
      colasPrimera: colasDelConductor,
      colasSegunda: [] as typeof colasDelConductor
    };
    const segundas = destinoActual.colasSegundaOpcion ?? [];
    const permitidas = destinoActual.colasPermitidas ?? [];
    return {
      colasPrimera: colasDelConductor.filter(c => permitidas.length ? permitidas.includes(c.id) : !segundas.includes(c.id)),
      colasSegunda: colasDelConductor.filter(c => segundas.includes(c.id))
    };
  }, [destinoActual, colasDelConductor]);
  const colaPorFicha = (ficha: string) => colasActivas.find(c => c.ficha === ficha);
  const camionPorPlaca = (placa: string) => camionesActivos.find(c => c.placa === placa);

  // Qué tiene de malo la combinación elegida. Se calcula en cada render para
  // poder avisar mientras se arma la orden, no solo al intentar guardarla.
  const problemas: string[] = [];
  if (destinoActual) {
    const camion = camionPorPlaca(form.placaCamion ?? '');
    if (camion && (camion.plantasVetadas ?? []).includes(destinoActual.id)) {
      problemas.push(`${camion.nombre} (${camion.placa}) no puede entrar a ${destinoActual.nombre}.`);
    }
    if (camion?.soloConductorId && camion.soloConductorId !== conductorActual?.id) {
      problemas.push(`${camion.nombre} solo lo puede llevar ${camion.soloConductorNombre}.`);
    }
    const cola = colaPorFicha(form.fichaCola ?? '');
    const permitidas = destinoActual.colasPermitidas ?? [];
    const segundas = destinoActual.colasSegundaOpcion ?? [];
    if (cola && permitidas.length && !permitidas.includes(cola.id) && !segundas.includes(cola.id)) {
      problemas.push(`${destinoActual.nombre} no admite la cola ${cola.ficha}.`);
    }
  }

  // Elegir el conductor arrastra su unidad: pase, placa y ficha de la cola.
  // Es el punto de todo el registro de flota: media orden se llena de un solo
  // clic, y sin teclear no hay erratas en un papel que sale de la empresa.
  const elegirConductor = (nombre: string) => {
    const c = conductoresActivos.find(x => x.nombre === nombre);
    setForm(prev => ({
      ...prev,
      conductor: nombre,
      paseChofer: c?.pase || prev.paseChofer,
      placaCamion: c?.camiones?.[0]?.placa || prev.placaCamion,
      fichaCola: c?.colas?.[0]?.ficha || prev.fichaCola
    }));
  };

  // Elegir la cola decide los galones del viaje: son suyos, no del chofer ni
  // del camión. La misma cola carga siempre lo mismo.
  const elegirCola = (ficha: string) => {
    const cola = colaPorFicha(ficha);
    setForm(prev => ({
      ...prev,
      fichaCola: ficha,
      lineas: cola?.capacidadGalones ? (prev.lineas ?? []).map((l, i) => i === 0 ? {
        ...l,
        galones: cola.capacidadGalones
      } : l) : prev.lineas
    }));
  };

  // Elegir el destino trae su RNC, que es el otro dato que se imprime.
  const elegirEnvasadora = (index: number, nombre: string) => {
    const e = envasadorasActivas.find(x => x.nombre === nombre);
    setLinea(index, {
      destino: nombre,
      rncDestino: e?.rnc ?? ''
    });
  };
  const setLinea = (index: number, patch: Partial<PedidoLinea>) => {
    setForm(prev => ({
      ...prev,
      lineas: (prev.lineas ?? []).map((l, i) => i === index ? {
        ...l,
        ...patch
      } : l)
    }));
  };
  // Qué falta para poder guardar. Se calcula en cada render para poder
  // señalar el campo concreto en vez de soltar un alert con una sola queja
  // a la vez.
  const faltantes: Record<string, string> = {};
  if (!form.numeroOrden?.trim()) faltantes.numeroOrden = 'Indica el número de orden';
  if (!form.fecha) faltantes.fecha = 'Indica la fecha del pedido';
  if (!form.conductor?.trim()) faltantes.conductor = 'Indica el conductor';
  if (!form.placaCamion?.trim()) faltantes.placaCamion = 'Indica la placa del camión';
  const lineasUtiles = (form.lineas ?? []).filter(l => (l.galones || 0) > 0);
  if (lineasUtiles.length === 0) {
    faltantes.lineas = esWOP(form.tipo ?? '') ? 'Registra al menos un compartimiento con galones' : 'Indica la cantidad de galones';
  } else if (lineasUtiles.some(l => !l.destino.trim())) {
    faltantes.lineas = 'Cada línea con galones necesita su destino';
  }
  const [mostrarFaltantes, setMostrarFaltantes] = useState(false);
  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    // Las reglas de flota se comprueban aquí también, no solo en pantalla: el
    // formulario se puede haber armado en otro orden y el aviso quedar fuera
    // de vista. Un documento con una combinación imposible no debe salir.
    if (problemas.length) {
      setMostrarFaltantes(true);
      return;
    }
    // Los errores solo se destapan al intentar guardar: marcar campos en rojo
    // apenas se abre el formulario es hostil.
    if (Object.keys(faltantes).length) {
      setMostrarFaltantes(true);
      return;
    }
    const base = {
      ...form,
      lineas: lineasUtiles
    } as Pedido;
    const galones = lineasUtiles.reduce((sum, l) => sum + (l.galones || 0), 0);
    const ok = await confirmar({
      titulo: editingId ? '¿Guardar los cambios?' : '¿Crear esta orden de pedido?',
      mensaje: editingId ? 'La orden se actualizará con los datos del formulario.' : 'La orden quedará en borrador hasta que alguien la autorice.',
      detalles: [{
        etiqueta: 'Orden',
        valor: base.numeroOrden
      }, {
        etiqueta: 'Formato',
        valor: etiquetaTipo(base.tipo)
      }, {
        etiqueta: 'Conductor',
        valor: base.conductor
      }, {
        etiqueta: 'Placa',
        valor: base.placaCamion
      }, {
        etiqueta: lineasUtiles.length === 1 ? 'Destino' : 'Destinos',
        valor: String(lineasUtiles.length)
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(galones)} galones`
      }],
      confirmar: editingId ? 'Guardar cambios' : 'Crear orden',
      tono: 'accent'
    });
    if (!ok) return;
    if (editingId) {
      onUpdate({
        ...base,
        id: editingId
      });
      onLog?.(`Orden de pedido editada: ${base.numeroOrden} (${etiquetaTipo(base.tipo)})`);
    } else {
      onAdd({
        ...base,
        id: Date.now().toString(),
        estado: 'borrador',
        autorizadoPor: '',
        autorizadoCargo: '',
        autorizadoFecha: '',
        // El token se acuña al autorizar, no al crear: un borrador todavía
        // no es un documento verificable.
        token: '',
        fecha_creacion: hoyISO()
      });
      onLog?.(`Orden de pedido creada: ${base.numeroOrden} (${etiquetaTipo(base.tipo)})`);
    }
    setMostrarFaltantes(false);
    cerrarForm();
  };

  // ── AUTORIZACIÓN ──────────────────────────────────────────────────────
  // Autorizar equivale a firmar el formulario: se estampa quién lo hizo y
  // desde ahí el pedido ya no se edita.
  const autorizar = async (p: Pedido) => {
    const ok = await confirmar({
      titulo: '¿Autorizar esta orden de pedido?',
      mensaje: `Tu nombre quedará estampado en el "AUTORIZADO POR" del formulario y se generará su código QR de verificación.`,
      detalles: [{
        etiqueta: 'Orden',
        valor: p.numeroOrden
      }, {
        etiqueta: 'Formato',
        valor: etiquetaTipo(p.tipo)
      }, {
        etiqueta: 'Conductor',
        valor: p.conductor || '—'
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(totalGalones(p))} galones`
      }, {
        etiqueta: 'Autoriza',
        valor: usuarioNombre
      }],
      advertencia: 'Una vez autorizada, la orden ya no se puede editar.',
      confirmar: 'Sí, autorizar',
      tono: 'ok'
    });
    if (!ok) return;
    onUpdate({
      ...p,
      estado: 'autorizado',
      autorizadoPor: usuarioNombre,
      autorizadoCargo: usuarioCargo,
      autorizadoFecha: hoyISO(),
      // Se acuña una sola vez: si la orden ya se autorizó antes (y se
      // reautoriza tras una corrección) el QR impreso sigue siendo válido.
      token: p.token || nuevoToken()
    });
    onLog?.(`Orden de pedido autorizada: ${p.numeroOrden} por ${usuarioNombre}`);
  };
  const anular = async (p: Pedido) => {
    const ok = await confirmar({
      titulo: '¿Anular esta orden?',
      mensaje: 'La orden queda registrada pero marcada como anulada. El formulario impreso saldrá con la marca "ANULADO" y quien escanee su QR verá que ya no es válida.',
      detalles: [{
        etiqueta: 'Orden',
        valor: p.numeroOrden
      }, {
        etiqueta: 'Total',
        valor: `${fmtGal(totalGalones(p))} galones`
      }],
      confirmar: 'Sí, anular',
      tono: 'warn'
    });
    if (!ok) return;
    onUpdate({
      ...p,
      estado: 'anulado'
    });
    onLog?.(`Orden de pedido anulada: ${p.numeroOrden}`);
  };
  const eliminar = async (p: Pedido) => {
    const ok = await confirmar({
      titulo: '¿Eliminar esta orden?',
      mensaje: 'Se borra del sistema y no queda registro de ella salvo en la auditoría.',
      detalles: [{
        etiqueta: 'Orden',
        valor: p.numeroOrden
      }, {
        etiqueta: 'Estado',
        valor: estadoStyle(p.estado).label
      }],
      advertencia: 'Esta acción no se puede deshacer. Si solo quieres invalidarla, usa Anular.',
      confirmar: 'Sí, eliminar',
      tono: 'danger'
    });
    if (!ok) return;
    onDelete(p.id);
    onLog?.(`Orden de pedido eliminada: ${p.numeroOrden}`);
  };

  // ── IMPRESIÓN ─────────────────────────────────────────────────────────
  const imprimir = (p: Pedido) => {
    imprimirPedido(p);
    onLog?.(`Orden de pedido impresa: ${p.numeroOrden}`);
  };

  // ── RENDER ────────────────────────────────────────────────────────────
  return <><PageShell title="Órdenes de Pedido" sub="Formulario de orden de pedido / retiro bajo contrato" action={canEdit ? <button onClick={abrirNuevo} style={primaryButton} data-id="element-837" data-mp="1a7o5r6hpkiol">
            <Plus size={15} data-id="element-838" data-mp="1f56fv07gp3y6" /> Nueva orden
          </button> : undefined} data-id="element-836" data-mp="15ev3cp9ppmfo">

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0,1fr))',
        gap: 14,
        marginBottom: 26
      }} data-id="element-839" data-mp="1f7kcvelpcq8d">

        <StatCard label="Órdenes" value={String(stats.total)} note="registradas" data-id="element-840" data-mp="mhajjwlt1e1x" />
        <StatCard label="Borradores" value={String(stats.borradores)} note="sin autorizar" accent={T.warn} data-id="element-841" data-mp="rf3s2wakyk5d" />

        <StatCard label="Autorizadas" value={String(stats.autorizados)} note="listas para despacho" accent={T.ok} data-id="element-842" data-mp="wgjdipgo6soa" />

        <StatCard label="Galones del mes" value={fmtGal(stats.galonesMes)} note="AG solicitados" accent={T.info} data-id="element-843" data-mp="22x4u9t27jffj" />

      </div>

      {/* ── FILTROS ── */}
      <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        marginBottom: 20
      }} data-id="element-844" data-mp="3scflu380ykbi">

        <div style={{
          position: 'relative',
          flex: '1 1 220px'
        }} data-id="element-845" data-mp="3ndl0ouml4jo8">

          <Search size={15} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-846" data-mp="3ikrx4a2urhdq" />

          <input style={{
            ...inputStyle,
            paddingLeft: 34
          }} placeholder="Buscar por orden, conductor, placa o destino" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-847" data-mp="39n952lqsk2sc" />

        </div>
        <select style={{
          ...inputStyle,
          flex: '0 1 190px'
        }} value={filterTipo} onChange={e => setFilterTipo(e.target.value as typeof filterTipo)} data-id="element-848" data-mp="2uof7v69q3tng">

          <option value="todos" data-id="element-849" data-mp="xu16trh8172s">Todos los formatos</option>
          {TIPOS_DISPONIBLES.map(t => <option key={t} value={t} data-id="element-850" data-mp="sxtzbu96fniq">
              {SUPLIDORES[t].etiqueta}
            </option>)}
          {hayWOP && <option value="WOP" data-id="element-851" data-mp="10vnj88u6den0">{SUPLIDORES.WOP.etiqueta}</option>}
        </select>
        <select style={{
          ...inputStyle,
          flex: '0 1 160px'
        }} value={filterEstado} onChange={e => setFilterEstado(e.target.value as typeof filterEstado)} data-id="element-852" data-mp="15wbamvmbfxhr">

          <option value="todos" data-id="element-853" data-mp="vxly7id6v58w">Todos los estados</option>
          <option value="borrador" data-id="element-854" data-mp="i78r1i84fukm">Borrador</option>
          <option value="autorizado" data-id="element-855" data-mp="i5nzm1h3srqz">Autorizado</option>
          <option value="anulado" data-id="element-856" data-mp="mxpabiymiaew">Anulado</option>
        </select>
      </div>

      {/* ── FILTRO POR FECHA ──
          Va en botones y no en otro desplegable porque son los cuatro cortes
          que se piden todos los días: un clic en vez de abrir y elegir. */}
      <div style={{
        display: 'flex',
        gap: 7,
        flexWrap: 'wrap',
        marginBottom: 20
      }} data-id="element-857" data-mp="d61n5u93nfj8">

        {RANGOS.map(r => {
          const activo = filterFecha === r.id;
          return <button key={r.id} type="button" onClick={() => setFilterFecha(r.id)} style={{
            padding: '9px 15px',
            borderRadius: 99,
            fontSize: 12.5,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            whiteSpace: 'nowrap',
            color: activo ? T.accent : T.textSecondary,
            background: activo ? T.accentSoft : 'transparent',
            border: `1px solid ${activo ? T.accentBorder : T.border}`,
            transition: 'all .15s'
          }} data-id="element-858" data-mp="3bswlc3mpwnp">

              {r.label}
              <span style={{
              fontSize: 11,
              fontWeight: 800,
              padding: '1px 7px',
              borderRadius: 99,
              background: activo ? 'rgba(255,255,255,.7)' : T.surfaceSubtle,
              color: activo ? T.accent : T.textMuted
            }} data-id="element-859" data-mp="ccc2rtapwujv">

                {conteoRango[r.id]}
              </span>
            </button>;
        })}
      </div>

      {/* ── FORMULARIO ── */}
      <AnimatePresence data-id="element-860" data-mp="3b23hixtwcj1y">
        {showForm && <motion.form onSubmit={guardar} initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} style={{
          ...glassLight,
          padding: isMobile ? 18 : 24,
          marginBottom: 24,
          overflow: 'hidden'
        }} data-id="element-861" data-mp="3f4qefd5fcu60">

            <SectionTitle data-id="element-862" data-mp="2nuwcatpeujt">
              {editingId ? 'Editar orden de pedido' : 'Nueva orden de pedido'}
            </SectionTitle>

            <Field label="Suplidor" data-id="element-863" data-mp="6qrksi5xdkvo">
              <div style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap'
            }} data-id="element-864" data-mp="6nlsyv5ww4w9">

                {/* WOP solo se ofrece si esta orden ya venía en ese formato:
                    las nuevas son siempre GLP, pero editar una vieja no debe
                    cambiarle el formato a la fuerza. */}
                {(esWOP(form.tipo ?? '') ? [...TIPOS_DISPONIBLES, 'WOP' as PedidoTipo] : TIPOS_DISPONIBLES).map(t => {
                const activo = normalizarTipo(form.tipo ?? '') === t;
                const Icono = ICONO_TIPO[t];
                return <button key={t} type="button" onClick={() => cambiarTipo(t)} style={{
                  ...(activo ? primaryButton : ghostButton),
                  flex: '1 1 180px',
                  justifyContent: 'center'
                }} data-id="element-865" data-mp="1w73lgr97726q">

                      <Icono size={15} data-id="element-866" data-mp="2jvwmv38la28q" />
                      {SUPLIDORES[t].etiqueta}
                    </button>;
              })}
              </div>
              <div style={{
              marginTop: 8,
              fontSize: 11.5,
              color: T.textMuted
            }} data-id="element-867" data-mp="2evid9grcak8l">

                El formulario sale dirigido a{' '}
                <strong style={{
                color: T.textSecondary
              }} data-id="element-868" data-mp="13jpv5c0094vl">

                  {SUPLIDORES[normalizarTipo(form.tipo ?? '')].razonSocial}
                </strong>
              </div>
            </Field>

            <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 16
          }} data-id="element-869" data-mp="2ynntqkgzyou7">

              <Field label="Orden de pedido número *" data-id="element-870" data-mp="2ply51mei2404">
                <CampoValidado modo="numeros" valor={form.numeroOrden ?? ''} onChange={v => setForm({
                ...form,
                numeroOrden: v
              })} placeholder="1587725" maxLength={15} data-id="element-871" data-mp="2ujg5eoegbawi" />

              </Field>
              <Field label="Fecha *" data-id="element-872" data-mp="3m9ugkcv2y94h">
                <input type="date" style={inputStyle} value={form.fecha ?? ''} onChange={e => setForm({
                ...form,
                fecha: e.target.value
              })} data-id="element-873" data-mp="3md06fubj9f19" />

              </Field>
              {/* El destino manda: de él dependen qué cola y qué camión se
                  pueden usar, así que se elige antes que nada. */}
              {!esWOP(form.tipo ?? '') && <div style={{
              gridColumn: isMobile ? 'auto' : '1 / -1'
            }} data-id="element-874" data-mp="9uthgjb2lrjq">
                  <Field label="Destino (envasadora) *" data-id="element-875" data-mp="j6hib5lmrxxt">
                    <select style={inputStyle} value={form.lineas?.[0]?.destino ?? ''} onChange={e => elegirEnvasadora(0, e.target.value)} data-id="element-876" data-mp="f7ightv74hal">
                      <option value="" data-id="element-877" data-mp="sxc2m4fbgngm">
                        {envasadorasActivas.length ? '— Elige el destino —' : '— Sin envasadoras registradas —'}
                      </option>
                      {envasadorasActivas.map(env => <option key={env.id} value={env.nombre} data-id="element-878" data-mp="b9iswqyec0a">
                          {env.nombre}{env.ciudad ? ` · ${env.ciudad}` : ''}
                        </option>)}
                      {!!form.lineas?.[0]?.destino && !envasadorasActivas.some(env => env.nombre === form.lineas![0].destino) && <option value={form.lineas[0].destino} data-id="element-879" data-mp="577zrz9ycrqo">
                          {form.lineas[0].destino} (no registrada)
                        </option>}
                    </select>
                    {destinoActual ? <div style={{
                  marginTop: 6,
                  fontSize: 11.5,
                  color: T.textMuted
                }} data-id="element-880" data-mp="1gpsnemqfutjm">
                        La cola y el camión de abajo ya salen filtrados por lo
                        que admite {destinoActual.nombre}.
                      </div> : null}
                  </Field>
                </div>}
              <Field label="Conductor del camión *" data-id="element-881" data-mp="2rpwyj2lbaggi">
                <select style={inputStyle} value={form.conductor ?? ''} onChange={e => elegirConductor(e.target.value)} data-id="element-882" data-mp="3oemicygi1j54">
                  <option value="" data-id="element-883" data-mp="3kd0zmwhaaan0">
                    {conductoresActivos.length ? '— Elige un conductor —' : '— Sin conductores registrados —'}
                  </option>
                  {conductoresActivos.map(c => <option key={c.id} value={c.nombre} data-id="element-884" data-mp="3ameq9k05ugcr">
                      {c.nombre}{c.pase ? ` · ${c.pase}` : ''}
                    </option>)}
                  {/* Chofer de una orden vieja que ya no está en la lista */}
                  {conductorFuera && <option value={form.conductor} data-id="element-885" data-mp="74o8ntjkak5d">
                      {form.conductor} (no registrado)
                    </option>}
                </select>
                {!conductoresActivos.length && puedeVerFlota ? <div style={{
                marginTop: 6,
                fontSize: 11.5,
                color: T.textMuted
              }} data-id="element-886" data-mp="jithh7zmw8hk">

                    Regístralos en <strong style={{
                  color: T.textSecondary
                }} data-id="element-887" data-mp="sj86oifpy7qc">Flota y Destinos</strong> y aparecerán aquí con su
                    placa, su cola y sus galones.
                  </div> : null}
              </Field>
              <Field label="Pase del chofer" data-id="element-888" data-mp="176kyuq017bpp">
                <CampoValidado modo="codigo" valor={form.paseChofer ?? ''} onChange={v => setForm({
                ...form,
                paseChofer: v
              })} placeholder="APP-87" maxLength={15} data-id="element-889" data-mp="177vxkbemdt9y" />

              </Field>
              {/* La cola sale filtrada por lo que admite la planta. Las de
                  segunda opción van en su propio grupo: se pueden usar, pero
                  no son la primera elección. */}
              <Field label="Ficha de la cola" data-id="element-890" data-mp="3h74e55xqxrpl">
                {colasActivas.length ? <select style={inputStyle} value={form.fichaCola ?? ''} onChange={e => elegirCola(e.target.value)} data-id="element-891" data-mp="3d9gig0o8buwn">
                    <option value="" data-id="element-892" data-mp="3r1d69siv7in8">— Elige la cola —</option>
                    {colasPrimera.map(c => <option key={c.id} value={c.ficha} data-id="element-893" data-mp="3m78zb99lnam5">
                        {c.ficha}{c.capacidadGalones ? ` · ${fmtGal(c.capacidadGalones)} gal` : ''}
                      </option>)}
                    {colasSegunda.length ? <optgroup label="Segunda opción" data-id="element-894" data-mp="3w0hdidm8b6ax">
                        {colasSegunda.map(c => <option key={c.id} value={c.ficha} data-id="element-895" data-mp="ds8i5abwdmd7">
                            {c.ficha}{c.capacidadGalones ? ` · ${fmtGal(c.capacidadGalones)} gal` : ''}
                          </option>)}
                      </optgroup> : null}
                    {/* La cola ya elegida no desaparece aunque el destino la
                        deje fuera: se muestra marcada y el aviso explica. */}
                    {!!form.fichaCola && ![...colasPrimera, ...colasSegunda].some(c => c.ficha === form.fichaCola) && <option value={form.fichaCola} data-id="element-896" data-mp="v1krxotuu1am">
                        {form.fichaCola} (no admitida aquí)
                      </option>}
                  </select> : <CampoValidado modo="codigo" valor={form.fichaCola ?? ''} onChange={v => setForm({
                ...form,
                fichaCola: v
              })} placeholder="58-01" maxLength={15} data-id="element-897" data-mp="lc14s6z3zl9y" />}

              </Field>
              <Field label="Placa del camión *" data-id="element-898" data-mp="cce4xpm12nj0">
                {camionesActivos.length ? <select style={inputStyle} value={form.placaCamion ?? ''} onChange={e => setForm({
                ...form,
                placaCamion: e.target.value
              })} data-id="element-899" data-mp="3uhppn9zi4iyb">
                    <option value="" data-id="element-900" data-mp="1sq4dp9o3dzyz">— Elige el camión —</option>
                    {camionesValidos.map(c => <option key={c.id} value={c.placa} data-id="element-901" data-mp="1wr6c5oa6q7zw">
                        {c.nombre} · {c.placa}
                      </option>)}
                    {!!form.placaCamion && !camionesValidos.some(c => c.placa === form.placaCamion) && <option value={form.placaCamion} data-id="element-902" data-mp="131fofiqgj1i9">
                        {form.placaCamion} (no permitido aquí)
                      </option>}
                  </select> : <CampoValidado modo="placa" valor={form.placaCamion ?? ''} onChange={v => setForm({
                ...form,
                placaCamion: v
              })} placeholder="L-259163" data-id="element-903" data-mp="y4fhz6pp3wqm" />}

              </Field>
            </div>


            <SectionTitle data-id="element-904" data-mp="kaxxnjp6qfzj">
              {esWOP(form.tipo ?? '') ? 'Detalle por compartimiento' : 'Detalle del despacho'}
            </SectionTitle>

            <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            marginBottom: 18
          }} data-id="element-905" data-mp="fa0i7evk801q">

              {lineas.map((linea, i) => <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : esWOP(form.tipo ?? '') ? '96px 1.3fr 100px 1.4fr 1fr' : '160px 1fr',
              gap: 10,
              alignItems: 'center',
              padding: '10px 12px',
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.rMd
            }} data-id="element-906" data-mp="5df8whcic2s3" data-mpi={i}>

                  {esWOP(form.tipo ?? '') && <span style={{
                fontSize: 12,
                fontWeight: 700,
                color: T.text
              }} data-id="element-907" data-mp="2qz85jatqvxuv">

                      Compart. {linea.compartimiento}
                    </span>}
                  {esWOP(form.tipo ?? '') && <select style={inputStyle} value={linea.producto} onChange={e => setLinea(i, {
                producto: e.target.value as ProductoWOP
              })} data-id="element-908" data-mp="1yb2ey7g0fen4">

                      {PRODUCTOS_WOP.map(prod => <option key={prod.id} value={prod.id} data-id="element-909" data-mp="281x6nrc0z6fy">
                          {prod.label}
                        </option>)}
                    </select>}
                  {/* Los galones los pone la cola. Se dejan editables por si
                      un viaje sale con menos de lo habitual. */}
                  <Field label={esWOP(form.tipo ?? '') ? '' : 'Galones'} data-id="element-910" data-mp="17hiqpyzlwf1s">
                    <CampoValidado modo="numeros" valor={linea.galones ? String(linea.galones) : ''} onChange={v => setLinea(i, {
                  galones: Number(v) || 0
                })} placeholder="Galones" maxLength={7} data-id="element-911" data-mp="13hifsobpulzh" />

                  </Field>

                  {/* En GLP el destino se elige arriba, una sola vez. En WOP
                      cada compartimiento puede ir a un sitio distinto. */}
                  {esWOP(form.tipo ?? '') && <select style={inputStyle} value={linea.destino} onChange={e => elegirEnvasadora(i, e.target.value)} data-id="element-912" data-mp="yixe0kgtfu8x">
                    <option value="" data-id="element-913" data-mp="tjb1kd8tai16">
                      {envasadorasActivas.length ? '— Elige el destino —' : '— Sin envasadoras registradas —'}
                    </option>
                    {envasadorasActivas.map(env => <option key={env.id} value={env.nombre} data-id="element-914" data-mp="24zsn3iavf091">
                        {env.nombre}
                      </option>)}
                    {/* Destino escrito a mano en una orden anterior */}
                    {!!linea.destino && !envasadorasActivas.some(env => env.nombre === linea.destino) && <option value={linea.destino} data-id="element-915" data-mp="3dwhbr3n1neos">
                        {linea.destino} (no registrada)
                      </option>}
                  </select>}

                  <Field label={esWOP(form.tipo ?? '') ? '' : 'RNC del destino'} data-id="element-916" data-mp="3iwvmitg152qi">
                    <CampoValidado modo="rnc" valor={linea.rncDestino} onChange={v => setLinea(i, {
                  rncDestino: v
                })} placeholder="RNC del destino" data-id="element-917" data-mp="ag0s82dasqo1" />

                  </Field>
                </div>)}
            </div>

            {esWOP(form.tipo ?? '') && <div style={{
            fontSize: 11,
            color: T.textMuted,
            marginBottom: 16,
            marginTop: -8
          }} data-id="element-918" data-mp="5n7pfk1e3sel">

                Los compartimientos sin galones no se imprimen en el formulario.
              </div>}

            <Field label="Observaciones" data-id="element-919" data-mp="2u7ofuz66nt9s">
              <input style={inputStyle} value={form.observaciones ?? ''} onChange={e => setForm({
              ...form,
              observaciones: e.target.value
            })} placeholder="Opcional" data-id="element-920" data-mp="2u8q2m0wka47c" />

            </Field>

            {/* Reglas de flota: se avisa mientras se arma la orden, sin
                esperar a que le den a guardar. */}
            {problemas.length ? <div style={{
            marginBottom: 14,
            padding: '12px 14px',
            borderRadius: 12,
            background: T.dangerSoft,
            border: `1px solid ${T.danger}33`,
            color: T.danger,
            fontSize: 12.5,
            lineHeight: 1.5
          }} data-id="element-921" data-mp="3d7c4vj3te1cu">

                <strong data-id="element-922" data-mp="2z7sxisituu1l">Esta combinación no se puede despachar:</strong>
                <ul style={{
              margin: '6px 0 0',
              paddingLeft: 18
            }} data-id="element-923" data-mp="1rtheg5p3vo52">
                  {problemas.map((p, i) => <li key={i} data-id="element-924" data-mp="1nqt5zz4b0j8m" data-mpi={i}>{p}</li>)}
                </ul>
              </div> : null}

            {mostrarFaltantes && Object.keys(faltantes).length ? <div style={{
            marginBottom: 14,
            padding: '12px 14px',
            borderRadius: 12,
            background: T.dangerSoft,
            border: `1px solid ${T.danger}33`,
            color: T.danger,
            fontSize: 12.5,
            fontWeight: 600,
            lineHeight: 1.6
          }} data-id="element-925" data-mp="37qtz3glj9kc">
              Falta completar: {Object.values(faltantes).join(' · ')}
            </div> : null}
            <div style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end'
          }} data-id="element-926" data-mp="qz60l8feod1e">

              <button type="button" onClick={cerrarForm} style={ghostButton} data-id="element-927" data-mp="v22nvcfw4xbo">
                Cancelar
              </button>
              <button type="submit" style={primaryButton} data-id="element-928" data-mp="zzb6tf8v55dz">
                {editingId ? 'Guardar cambios' : 'Crear orden'}
              </button>
            </div>
          </motion.form>}
      </AnimatePresence>

      {/* ── LISTADO ── */}
      {filtrados.length === 0 ? <EmptyState title="Sin órdenes de pedido" hint={canEdit ? 'Crea la primera orden para imprimir el formulario de Refidomsa.' : 'Tu rol no permite crear órdenes de pedido.'} icon={<FileText size={22} data-id="element-930" data-mp="3nfspoyemi7sc" />} data-id="element-929" data-mp="3di4rtzlq0k5y" /> : <>
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12
        }} data-id="element-931" data-mp="3ngubnxx6geou">

            {pageItems.map(p => {
            const es = estadoStyle(p.estado);
            const bloqueado = p.estado !== 'borrador';
            return <motion.div key={p.id} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} style={{
              ...glassLight,
              padding: isMobile ? 16 : '18px 20px',
              display: 'flex',
              gap: 16,
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              flexWrap: 'wrap'
            }} data-id="element-932" data-mp="2zpounffi3gcy">

                  <div style={{
                flex: '1 1 260px',
                minWidth: 0
              }} data-id="element-933" data-mp="y42qfyqi3j85">

                    <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                  marginBottom: 6
                }} data-id="element-934" data-mp="u27msnl3ktr7">

                      <span style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: T.text
                  }} data-id="element-935" data-mp="k3s0cxx0m8o1">

                        Orden {p.numeroOrden || '—'}
                      </span>
                      <Badge label={etiquetaTipo(p.tipo)} color={COLOR_TIPO[normalizarTipo(p.tipo)].color} soft={COLOR_TIPO[normalizarTipo(p.tipo)].soft} dot={false} data-id="element-936" data-mp="6d6ed0c9lilx" />

                      <Badge label={es.label} color={es.color} soft={es.soft} data-id="element-937" data-mp="1fwppmwp9az5" />
                    </div>
                    <div style={{
                  fontSize: 12,
                  color: T.textSecondary
                }} data-id="element-938" data-mp="3smhbos7wdspb">

                      {fmtFecha(p.fecha)} · {p.conductor || 'Sin conductor'} ·{' '}
                      {p.placaCamion || 'Sin placa'}
                    </div>
                    <div style={{
                  fontSize: 12,
                  color: T.textMuted,
                  marginTop: 3
                }} data-id="element-939" data-mp="3iy2cffe896be">

                      {p.lineas.length}{' '}
                      {p.lineas.length === 1 ? 'destino' : 'destinos'} ·{' '}
                      {fmtGal(totalGalones(p))} galones
                      {p.estado === 'autorizado' && p.autorizadoPor ? ` · Autorizó ${p.autorizadoPor}` : ''}
                    </div>
                  </div>

                  <div style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap'
              }} data-id="element-940" data-mp="3nvc6578ng451">

                    <button type="button" onClick={() => imprimir(p)} style={iconBtn} title="Imprimir formulario" data-id="element-941" data-mp="jgu8mf2osfn7">

                      <Printer size={14} data-id="element-942" data-mp="o9x813u5ajbb" />
                    </button>

                    {canAutorizar && p.estado === 'borrador' && <button type="button" onClick={() => autorizar(p)} style={{
                  ...iconBtn,
                  borderColor: `${T.ok}55`,
                  color: T.ok
                }} title="Autorizar orden" data-id="element-943" data-mp="28woelvyy3qsg">

                        <ShieldCheck size={14} data-id="element-944" data-mp="2dpr9q8jdf8ar" />
                      </button>}

                    {canEdit && !bloqueado && <button type="button" onClick={() => abrirEdicion(p)} style={iconBtn} title="Editar" data-id="element-945" data-mp="1pzdjmnkcjjit">

                        <Edit2 size={14} data-id="element-946" data-mp="1l1lshvrj01fo" />
                      </button>}

                    {canArchive && p.estado === 'autorizado' && <button type="button" onClick={() => anular(p)} style={{
                  ...iconBtn,
                  borderColor: `${T.warn}55`,
                  color: T.warn
                }} title="Anular orden" data-id="element-947" data-mp="1zyunczg1fnc5">

                        <Ban size={14} data-id="element-948" data-mp="3bfcimw7wfjx7" />
                      </button>}

                    {canArchive && p.estado !== 'autorizado' && <button type="button" onClick={() => eliminar(p)} style={{
                  ...iconBtn,
                  borderColor: `${T.danger}55`,
                  color: T.danger
                }} title="Eliminar" data-id="element-949" data-mp="1fx1em3zwdhv4">

                        <Trash2 size={14} data-id="element-950" data-mp="1oxgd651vgtgk" />
                      </button>}
                  </div>

                </motion.div>;
          })}
          </div>

          <Pagination page={page} onChange={setPage} totalPages={totalPages} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="órdenes" data-id="element-951" data-mp="1tuq0nf5pav1r" />

        </>}
    </PageShell>
    {dialogo}
  </>;
}